import { Ban, Cloud, X } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import {
  ExpandableMessageCard,
  SimpleMessageCard,
  ToolError,
  isBootsmanResult,
} from "./shared";

type AbortDeploymentResult = {
  deploymentId: string;
  errorMessage?: string;
};

interface DeploymentAbortProps {
  messageId: string;
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

export function DeploymentAbort({ part }: DeploymentAbortProps) {
  const isError = part.status === "error";

  // Extract abort result if available
  const result = (() => {
    if (
      part.status === "complete" &&
      part.result &&
      isBootsmanResult(part.result) &&
      part.result.success
    ) {
      return part.result.data as AbortDeploymentResult;
    }
    return null;
  })();

  const wasAborted = result && !result.errorMessage;

  const getStatusText = () => {
    if (part.status === "pending") {
      return "Aborting Deploy...";
    }

    if (isError) {
      return "Failed to Abort Deploy";
    }

    if (result?.errorMessage) {
      return `Failed to Abort: ${result.errorMessage}`;
    }

    if (wasAborted) {
      return "Deploy Aborted";
    }

    return "Deploy Abort Attempted";
  };

  // Show simple card for successful abort
  if (wasAborted && !result.errorMessage) {
    return (
      <SimpleMessageCard
        status={part.status}
        icon={Cloud}
        title={<div className="flex items-center gap-2">{getStatusText()}</div>}
      />
    );
  }

  // Show expandable card for errors or detailed info
  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Cloud}
      title={getStatusText()}
      accordionValue="deployment-abort"
    >
      {/* Show abort details */}
      {result && (
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">Deploy ID:</span>
            <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">
              {result.deploymentId}
            </code>
          </div>

          {result.errorMessage && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700">
              <div className="flex items-center gap-2">
                <X className="w-4 h-4" />
                <span className="font-medium">Abort Failed</span>
              </div>
              <div className="mt-1 text-sm">{result.errorMessage}</div>
            </div>
          )}

          {!result.errorMessage && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-700">
              <div className="flex items-center gap-2">
                <Ban className="w-4 h-4" />
                <span className="font-medium">
                  Deploy Cancelled Successfully
                </span>
              </div>
              <div className="mt-1 text-sm">
                The deployment has been cancelled and will stop processing.
              </div>
            </div>
          )}
        </div>
      )}

      {/* Show tool execution error */}
      {isError && part.result ? <ToolError result={part.result} /> : null}
    </ExpandableMessageCard>
  );
}
